import { useEffect, useRef, useState } from 'react'
import merchantApi from '../../services/merchantApi'
import './ProfilePage.scss'
import Header from '../../components/Header/Header'
import { Avatar } from '@mui/material'
import PhoneInputField from '../../components/PhoneInputField/PhoneInputField'
import parsePhoneNumberFromString from 'libphonenumber-js'
const unwrapProfile = (response) => response?.data?.user || response?.data || response?.user || response || {}

const formatDate = (value) => {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return String(value)
  return date.toLocaleDateString('en-MY', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

export default function ProfilePage() {
  const [profile, setProfile] = useState()
  const [loading, setLoading] = useState(false)
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [error, setError] = useState(null);

  const fileInputRef = useRef(null);

  useEffect(() => {
    const loadProfile = async () => {
      try {
        setLoading(true)
        const response = await merchantApi.auth.me()
        const user = unwrapProfile(response)
        const parsePhoneNumber = parsePhoneNumberFromString(user.phone ? `+${user.phone}` : '')
        setProfile({
          merchantName: user.merchant?.business_name || '',
          name: user.name || user.full_name || '',
          phone: parsePhoneNumber?.nationalNumber || user.phone || '',
          countryCode: parsePhoneNumber?.countryCallingCode || '60',
          email: user.email || '',
          role: user.role?.display_name || user.role?.name || user.role_name,
          joinedDate: formatDate(user.created_at || user.joined_at || user.member_since),
          avatar: user.profile_photo_url || user.profile_photo_path || '',
          outlets: Array.isArray(user.outlets) && user.outlets.length > 0
            ? user.outlets.map((outlet) => ({
              name: outlet.name || outlet.outlet_name || 'Outlet',
              role: outlet.role || outlet.position || 'Owner',
              address: outlet.address || outlet.address_line_1 || outlet.full_address || '-',
            }))
            : [],
        })
      } catch (error) {
        setError(error.message || 'Unable to load profile information.')
      } finally {
        setLoading(false)
      }
    }

    loadProfile()
  }, [])

  const handleUploadPhoto = (event) => {
    const file = event.target.files?.[0];

    if (!file) return;

    setSelectedPhoto(file);

    setProfile((prev) => ({
      ...prev,
      avatar: URL.createObjectURL(file),
    }));

    event.target.value = '';
  };
  console.log(selectedPhoto)
  const handleRemovePhoto = async () => {
    try {
      setLoading(true);
      await merchantApi.profile.removePhoto();
      setProfile((prev) => ({
        ...prev,
        avatar: '',
      }));
    } catch (error) {
      console.error(error);
      setError(error.message || 'Unable to remove photo.');
    } finally {
      setLoading(false);
    }
  };

  const handleProfileUpdate = async () => {
    setLoading(true);

    try {
      // Update profile information
      await merchantApi.profile.update({
        name: profile.name,
        phone: profile.phone ? `${profile.countryCode}${profile.phone}` : '',
        email: profile.email,
      });

      // Upload photo only if user selected one
      if (selectedPhoto) {
        await merchantApi.profile.updatePhoto(selectedPhoto);
        setSelectedPhoto(null);
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      setError(error.message || 'Unable to update profile.');
    } finally {
      setLoading(false);
      window.location.reload(); // Refresh the page to reflect changes
    }
  };

  const handleProfileOnChange = (field, e) => {
    setProfile({ ...profile, [field]: e.target.value })
  }

  return (
    <div className="profile-page">
      <Header
        items={[
          { label: profile?.merchantName || 'Merchant', to: '/' },
          { label: 'Profile', current: true },
        ]}
      />
      <div className="profile-page__header">
        <div>
          <h1>Profile</h1>
          <p>Manage your personal info, security and notification preferences</p>
        </div>

        <button
          className="profile-page__save"
          onClick={handleProfileUpdate}
          type="button"
          disabled={loading}
          title="Profile update endpoint is not defined in the current collection"
        >
          {loading ? 'Loading...' : 'Save changes'}
        </button>
      </div>

      {error && <div className="payment-alert payment-alert--error">{error}</div>}

      <div className="profile-page__grid">
        <div className="profile-page__main">
          <section className="profile-card">
            <div className="profile-card__header">
              Personal information
            </div>

            <div className="profile-card__body">
              <div className="form-group">
                <label className="form-label">Name</label>
                <input className="form-input" value={profile?.name} onChange={(e) => handleProfileOnChange('name', e)} />
              </div>

              <div className="profile-page__form-row">
                <div className="sr-modal__field">
                  <label>Phone number</label>
                  <PhoneInputField
                    value={profile?.phone}
                    onChange={(value) => handleProfileOnChange('phone', { target: { value } })}
                    countryCode={profile?.countryCode || '60'}
                    onCountryCodeChange={(value) => handleProfileOnChange('countryCode', { target: { value } })}
                    className="profile-page__phone-input"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">
                    Email address <span className="form-label--optional">(optional)</span>
                  </label>
                  <input className="form-input" placeholder="Email address" value={profile?.email} onChange={(e) => handleProfileOnChange('email', e)} />
                </div>
              </div>
            </div>
          </section>

          <section className="profile-card">
            <div className="profile-card__header">
              Work details
            </div>

            <div className="profile-card__body">
              <div className="profile-page__form-row">
                <div className="form-group">
                  <label className="form-label">Role</label>
                  <input className="form-input" value={profile?.role} readOnly />
                </div>

                <div className="form-group">
                  <label className="form-label">Member since</label>
                  <input className="form-input" value={profile?.joinedDate} readOnly />
                </div>
              </div>

              <div className="profile-page__outlets">
                <label className="form-label">Outlet(s) managed</label>

                {profile?.outlets?.length > 0 ? profile.outlets.map((outlet) => (
                  <div className="profile-page__outlet" key={outlet.name}>
                    <div>
                      <span>{outlet.name}</span>|
                      <span>{outlet.role}</span>
                    </div>
                    <p>{outlet.address}</p>
                  </div>
                ))
                  : <p className="profile-page__outlet-empty">No outlets found</p>}
              </div>
            </div>
          </section>
        </div>

        <aside className="profile-card profile-page__aside">
          <div className="profile-card__header">
            Outlet information
          </div>

          <div className="profile-page__photo-body">
            <div >
              {
                profile?.avatar ?
                  <Avatar className="profile-page__mui-avatar" src={profile.avatar} alt="Profile photo" />
                  : <Avatar className="profile-page__mui-avatar" />
              }
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/jpg,image/webp"
              hidden
              onChange={handleUploadPhoto}
            />
            <strong>{profile?.name}</strong>

            <div className="profile-page__photo-actions">
              <button onClick={() => fileInputRef.current?.click()}>Upload photo</button>
              <button className="profile-page__remove" onClick={handleRemovePhoto}>Remove</button>
            </div>

            <p>JPG, PNG or WEBP • max 2MB •</p>
            <p>600x600 recommended</p>
          </div>
        </aside>
      </div>
    </div>
  )
}